import { useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import { supportApi } from '@/api/support';
import { Danger, Spinner, ShieldTick, Cross } from '@/components/icons';
import { AppDialogLayout } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useModalContext } from '@/components/ui/modal-provider';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { SUPPORT_CATEGORY_OPTIONS } from '../constants';
import type {
    CreateSupportTicketInput,
    SupportTechnicalContext,
    SupportTicketCategory,
    SupportTicketDetail,
} from '@/api/support';

type SupportTicketFormProps = {
    onCreated: (ticket: SupportTicketDetail) => void;
};

const RequiredAsterisk = () => (
    <>
        <span className="text-destructive" aria-hidden="true">
            *
        </span>
        <span className="sr-only"> obrigatório</span>
    </>
);

const getTechnicalContext = (): SupportTechnicalContext | null => {
    if (typeof window === 'undefined') return null;

    return {
        browser: window.navigator.userAgent,
        language: window.navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
    };
};

export function SupportTicketForm({ onCreated }: SupportTicketFormProps) {
    const { closeModal } = useModalContext();
    const [category, setCategory] = useState<SupportTicketCategory>('bug');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [showPrivacyNotice, setShowPrivacyNotice] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const canSubmit = subject.trim().length >= 3 && body.trim().length > 0 && !isSubmitting;

    const handleSubmit = async () => {
        if (!canSubmit) return;

        const payload: CreateSupportTicketInput = {
            category,
            subject: subject.trim(),
            body: body.trim(),
            current_url:
                typeof window === 'undefined'
                    ? null
                    : `${window.location.pathname}${window.location.search}`,
            technical_context: getTechnicalContext(),
            pii_acknowledged: true,
        };

        setIsSubmitting(true);
        try {
            const ticket = await supportApi.createTicket(payload);
            toast.success('Chamado enviado ao suporte.');
            closeModal();
            onCreated(ticket);
        } catch (error) {
            console.error('Error creating support ticket:', error);
            toast.error('Não foi possível abrir o chamado agora.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        // Nested layout: the modal shell owns the title, this one owns the
        // footer, so the primary action stays pinned instead of sitting below
        // the fold of a scrolling body on a phone.
        <AppDialogLayout
            bodyClassName="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 pb-6"
            footer={
                <div className="flex items-center gap-3">
                    <div className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
                        <ShieldTick className="size-3.5" />
                        Canal interno do LaudoPsi
                    </div>
                    <div className="ml-auto flex gap-3">
                        <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSubmit} disabled={!canSubmit}>
                            {isSubmitting ? (
                                <>
                                    <Spinner className="size-4 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                'Abrir chamado'
                            )}
                        </Button>
                    </div>
                </div>
            }
        >
            {showPrivacyNotice ? (
                <div className="rounded-xl border border-warning/25 bg-warning/10 p-4">
                    <div className="flex gap-3">
                        <Danger className="mt-0.5 size-4 shrink-0 text-warning" />
                        <div className="min-w-0 flex-1 space-y-1 text-sm">
                            <p className="font-medium text-foreground">
                                Proteja os dados dos seus pacientes
                            </p>
                            <p className="leading-relaxed text-muted-foreground">
                                Descreva o problema sem incluir nomes de pacientes, documentos,
                                laudos, instrumentos, prints ou informações clínicas identificáveis.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="iconXs"
                            aria-label="Fechar aviso"
                            className="-mr-1 -mt-1 shrink-0 text-muted-foreground hover:bg-warning/15 hover:text-foreground [&_svg]:size-4"
                            onClick={() => setShowPrivacyNotice(false)}
                        >
                            <Cross className="size-4" />
                        </Button>
                    </div>
                </div>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label className="inline-flex items-center gap-1">
                        Categoria
                        <RequiredAsterisk />
                    </Label>
                    <Select
                        value={category}
                        onValueChange={(value: string) =>
                            setCategory(value as SupportTicketCategory)
                        }
                    >
                        <SelectTrigger aria-required="true">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {SUPPORT_CATEGORY_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="support-subject" className="inline-flex items-center gap-1">
                        Assunto
                        <RequiredAsterisk />
                    </Label>
                    <Input
                        id="support-subject"
                        maxLength={140}
                        required
                        aria-required="true"
                        placeholder="Ex.: Erro ao exportar laudo"
                        value={subject}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setSubject(event.target.value)
                        }
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="support-body" className="inline-flex items-center gap-1">
                    Descrição
                    <RequiredAsterisk />
                </Label>
                <Textarea
                    id="support-body"
                    maxLength={4000}
                    required
                    aria-required="true"
                    rows={4}
                    className="min-h-28 resize-none md:min-h-44"
                    placeholder="Conte o que aconteceu, em qual tela estava, qual ação tentou fazer e se apareceu alguma mensagem de erro."
                    value={body}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                        setBody(event.target.value)
                    }
                />
            </div>
        </AppDialogLayout>
    );
}
